import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container,
    Paper,
    Typography,
    Box,
    Button,
    Grid,
    Chip,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
    Switch,
    FormControlLabel
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SchoolIcon from '@mui/icons-material/School';
import PaymentIcon from '@mui/icons-material/Payment';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import axios from '../../utils/axios';
import PayPalButton from '../payments/PayPalButton';

const ExamDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [exam, setExam] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [subscriptionStatus, setSubscriptionStatus] = useState(null);
    const [showSubscriptionDialog, setShowSubscriptionDialog] = useState(false);
    const [showCancelDialog, setShowCancelDialog] = useState(false);
    const [updatingSubscription, setUpdatingSubscription] = useState(false);
    const [successMessage, setSuccessMessage] = useState(null);
    const [autoRenew, setAutoRenew] = useState(true);
    const [paypalMode, setPaypalMode] = useState('sandbox');

    useEffect(() => {
        const fetchExamDetails = async () => {
            try {
                const [examRes, subscriptionRes, settingsRes] = await Promise.all([
                    axios.get(`/api/exams/${id}`),
                    axios.get(`/api/subscriptions/status/${id}`),
                    axios.get('/api/settings/payment')
                ]);
                setExam(examRes.data);
                console.log('Exam data:', examRes.data);
                console.log('Subscription response:', subscriptionRes.data);
                // Set subscription status based on the response
                setSubscriptionStatus(subscriptionRes.data);
                // Set PayPal mode from settings
                setPaypalMode(settingsRes.data.mode);
            } catch (err) {
                console.error('Error fetching exam details:', err);
                setError(err.response?.data?.message || 'Error fetching exam details');
            } finally {
                setLoading(false);
            }
        };

        fetchExamDetails();
    }, [id]);

    const handleStartExam = async () => {
        if (!subscriptionStatus?.isActive && exam.price > 0) {
            setShowSubscriptionDialog(true);
            return;
        }

        navigate(`/exams/${id}/take`);
    };

    const handleSubscribe = async (paymentData) => {
        try {
            console.log('Payment data received:', paymentData);
            setUpdatingSubscription(true);
            
            const subscriptionData = {
                examId: id,
                autoRenew: autoRenew
            };
            
            // If the exam is free, set isFree to true
            if (exam.price === 0) {
                subscriptionData.isFree = true;
            } else if (paymentData) {
                // For paid exams, include payment data
                subscriptionData.paymentData = paymentData;
            } else {
                console.error('No payment data received for paid exam');
                setError('Payment processing error: No payment data received');
                setUpdatingSubscription(false);
                return;
            }
            
            console.log('Sending subscription data to server:', subscriptionData);
            
            const response = await axios.post('/api/subscriptions', subscriptionData);
            console.log('Subscription response:', response.data);
            
            // Update subscription status immediately after successful subscription
            setSubscriptionStatus({
                isActive: true,
                subscription: response.data
            });
            
            setError(null); // Clear any previous errors
            setShowSubscriptionDialog(false); // Close the subscription dialog
            
            // Show success message
            setSuccessMessage('Successfully subscribed to the exam!');
            
            // Refresh the page to update the UI
            window.location.reload();
            
        } catch (err) {
            console.error('Subscription error:', err);
            setError(err.response?.data?.message || 'Error creating subscription. Please try again.');
        } finally {
            setUpdatingSubscription(false);
        }
    };

    const handleToggleAutoRenew = async () => {
        try {
            const response = await axios.put(`/api/subscriptions/${id}/auto-renew`, {
                autoRenew: !autoRenew
            });
            setAutoRenew(!autoRenew);
            setSuccessMessage('Auto-renewal updated successfully');
        } catch (error) {
            console.error('Error updating auto-renewal:', error);
            setError('Failed to update auto-renewal. Please try again.');
        }
    };

    const handleCancelSubscription = async () => {
        try {
            setUpdatingSubscription(true);
            await axios.put(`/api/subscriptions/cancel/${subscriptionStatus.subscription._id}`);
            setSubscriptionStatus(prev => ({
                ...prev,
                subscription: {
                    ...prev.subscription,
                    autoRenew: false
                }
            }));
            setShowCancelDialog(false);
        } catch (err) {
            setError(err.response?.data?.message || 'Error cancelling subscription');
        } finally {
            setUpdatingSubscription(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-GB', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Container>
                <Alert severity="error" sx={{ mt: 2 }}>
                    {error}
                </Alert>
            </Container>
        );
    }

    return (
        <Container sx={{ py: 4 }}>
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/exams')}
                sx={{ mb: 2 }}
            >
                Back to Exams
            </Button>

            <Paper elevation={3} sx={{ p: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    {exam.title || exam.name}
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                    <Chip 
                        icon={<SchoolIcon />}
                        label={exam.category}
                        color="primary"
                        variant="outlined"
                    />
                    {exam.price > 0 && (
                        <Chip 
                            icon={<PaymentIcon />}
                            label={`Monthly Subscription: £${Number(exam.price).toFixed(2)}`}
                            color="success"
                            variant="outlined"
                        />
                    )}
                </Box>

                <Typography variant="body1" paragraph>
                    {exam.description}
                </Typography>

                <Divider sx={{ my: 3 }} />

                <Typography variant="h6" gutterBottom>
                    Exam Details
                </Typography>

                <List>
                    <ListItem>
                        <ListItemIcon>
                            <CheckCircleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText 
                            primary="Number of Questions" 
                            secondary={exam.numberOfQuestions}
                        />  
                    </ListItem>
                    {exam.price > 0 && (
                        <>
                            <ListItem>
                                <ListItemIcon>
                                    <PaymentIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText 
                                    primary="Monthly Subscription" 
                                    secondary={`£${Number(exam.price).toFixed(2)}`}
                                />
                            </ListItem>
                            {subscriptionStatus?.isActive && (
                                <>
                                    <ListItem>
                                        <ListItemIcon>
                                            <CheckCircleIcon color="success" />
                                        </ListItemIcon>
                                        <ListItemText 
                                            primary="Subscription Status" 
                                            secondary={`Active until ${formatDate(subscriptionStatus.subscription.endDate)}`}
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon>
                                            <PaymentIcon color="primary" />
                                        </ListItemIcon>
                                        <ListItemText 
                                            primary="Auto-Renew" 
                                            secondary="Automatically renew subscription when it expires"
                                        />
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={autoRenew}
                                                    onChange={handleToggleAutoRenew}
                                                    disabled={updatingSubscription}
                                                />
                                            }
                                            label={autoRenew ? "Enabled" : "Disabled"}
                                        />
                                    </ListItem>
                                </>
                            )}
                        </>
                    )}
                    {exam.price === 0 && (
                        <>
                            <ListItem>
                                <ListItemIcon>
                                    <PaymentIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText 
                                    primary="Monthly Subscription" 
                                    secondary="Free"
                                />
                            </ListItem>
                        </>
                    )}
                </List>

                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 2 }}>
                    {subscriptionStatus?.isActive && subscriptionStatus?.subscription && (
                        <>
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={() => setShowCancelDialog(true)}
                                disabled={updatingSubscription}
                            >
                                Cancel Subscription
                            </Button>
                            <Button
                                variant="contained"
                                size="large"
                                onClick={() => navigate(`/exams/${id}/take`)}
                                disabled={loading}
                            >
                                Start Exam
                            </Button>
                        </>
                    )}
                    {!subscriptionStatus?.isActive && (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={async () => {
                                if (exam.price > 0) {
                                    // For paid exams, show the subscription dialog
                                    setShowSubscriptionDialog(true);
                                } else {
                                    // For free exams, subscribe directly
                                    try {
                                        const response = await axios.post('/api/subscriptions', {
                                            examId: id,
                                            autoRenew: false,
                                            isFree: true
                                        });
                                        console.log('Subscription created:', response.data);
                                        // Update subscription status immediately after successful subscription
                                        setSubscriptionStatus({
                                            isActive: true,
                                            subscription: response.data
                                        });
                                        setSuccessMessage('Successfully subscribed to the exam!');
                                        
                                    } catch (error) {
                                        console.error('Error subscribing to exam:', error);
                                        setError('Failed to subscribe to the exam. Please try again.');
                                    }
                                }
                            }}
                            disabled={loading}
                            sx={{ mt: 2 }}
                        >
                            {exam.price > 0 ? 'Subscribe to Exam' : 'Subscribe to Exam'}
                        </Button>
                    )}
                </Box>
            </Paper>

            <Dialog
                open={showSubscriptionDialog}
                onClose={() => setShowSubscriptionDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Subscribe to {exam.title || exam.name}</DialogTitle>
                <DialogContent>
                    <Typography variant="h6" gutterBottom>
                        Monthly subscription: £{Number(exam.price).toFixed(2)}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
                        {/* Demo payment option - only shown in sandbox mode */}
                        {paypalMode === 'sandbox' && (
                            <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                                <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                                    Demo Mode Payment
                                </Typography>
                                <Typography variant="body2" paragraph>
                                    Complete a test transaction with no actual payment processed.
                                </Typography>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => {
                                        handleSubscribe({ id: `manual-${Date.now()}`, status: 'COMPLETED' });
                                    }}
                                    sx={{ mt: 1 }}
                                    disabled={updatingSubscription}
                                    fullWidth
                                >
                                    Complete Test Payment (£{Number(exam.price).toFixed(2)})
                                </Button>
                            </Box>
                        )}
                        
                        {paypalMode === 'sandbox' && (
                            <Divider>
                                <Typography variant="body2" color="text.secondary">
                                    OR
                                </Typography>
                            </Divider>
                        )}
                        
                        {/* PayPal payment option */}
                        <Box>
                            <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                                Pay with PayPal or Card
                            </Typography>
                            <PayPalButton
                                amount={exam.price}
                                description={`Monthly subscription to ${exam.title || exam.name}`}
                                onSuccess={(data) => handleSubscribe(data)}
                                onError={(error) => {
                                    console.error('PayPal error:', error);
                                }}
                                disabled={updatingSubscription}
                                examId={id}
                                autoRenew={autoRenew}
                            />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowSubscriptionDialog(false)} color="inherit">Cancel</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={showCancelDialog}
                onClose={() => setShowCancelDialog(false)}
            >
                <DialogTitle>Cancel Subscription</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to cancel your subscription? You will still have access until {subscriptionStatus?.subscription?.endDate ? formatDate(subscriptionStatus.subscription.endDate) : 'the end of your current billing period'}.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowCancelDialog(false)}>No, Keep Subscription</Button>
                    <Button 
                        onClick={handleCancelSubscription} 
                        variant="contained" 
                        color="error"
                        disabled={updatingSubscription}
                    >
                        {updatingSubscription ? 'Cancelling...' : 'Yes, Cancel Subscription'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default ExamDetail; 